import React, { useEffect, useMemo, useRef, useState } from 'react';
import { get } from '@/features/shared/lib/api';
import ForceNetwork from '@/features/researcher/components/charts/ForceNetwork';
import PnuCoResearchers from '@/features/researcher/components/PnuCoResearchers';
import ExternalCoResearchers from '@/features/researcher/components/ExternalCoResearchers';

/**
 * ResearcherCoResearchers
 * - Full Co-Researchers tab content component.
 * - Props: active (boolean), className, record (optional for future data binding)
 */
export default function ResearcherCoResearchers({ active, className = '', record }) {
  const base = `${active ? 'active' : 'hidden'} pt-[50px] lg:pt-[60px]`;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [internal, setInternal] = useState([]);
  const [external, setExternal] = useState([]);
  const didFetchRef = useRef(false);

  // Fetch coworker once when tab becomes active
  useEffect(() => {
    const scholarId = record?.scholar_id;
    if (!active || !scholarId || didFetchRef.current) return;

    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        // one-time fetch
        const data = await get(`/rm/v1/researcher/${encodeURIComponent(scholarId)}/coworker`, {
          params: { size: 30 },
        });
        if (!alive) return;
        const ok = data?.status === true || data?.success === true;
        const res = data?.result || data;
        const inArr = res?.internal || [];
        const exArr = res?.external || [];
        if (ok) {
          setInternal(Array.isArray(inArr) ? inArr : []);
          setExternal(Array.isArray(exArr) ? exArr : []);
          didFetchRef.current = true;
        } else {
          setError(res?.message || 'Failed to load coworker data');
          setInternal([]);
          setExternal([]);
        }
      } catch (e) {
        if (!didFetchRef.current) {
          setError(e?.response?.data?.message || e?.message || 'Failed to load coworker data');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [active, record?.scholar_id]);

  // External pagination (local)
  const [extPage, setExtPage] = useState(1);
  const perPage = 6;
  const totalExt = external?.length || 0;
  const totalExtPages = Math.max(1, Math.ceil(totalExt / perPage));
  const extPageSafe = Math.min(Math.max(1, extPage), totalExtPages);
  const extStart = (extPageSafe - 1) * perPage;
  // const extSlice = useMemo(() => (Array.isArray(external) ? external.slice(extStart, extStart + perPage) : []), [external, extStart]);
  // const orgText = (row) => row?.org_name || row?.authority_org_name || '';
  // const num = (v) => { const n = Number(v || 0) || 0; try { return n.toLocaleString(); } catch { return String(n); } };

  /** Network 전체 보기로 걸린 body 스크롤 잠금이 남는 경우, 이 구역에서 링크 클릭 시 복구 */
  const releaseBodyScrollOnLinkClick = (e) => {
    const t = e.target;
    if (typeof t?.closest !== 'function') return;
    const a = t.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href === '' || href.startsWith('#')) return;
    document.body.style.overflow = '';
  };

  return (
    <div className={`${base} ${className}`.trim()}>
      <div>
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Social network of PNU co-Researcher</h3>
        <div
          className="p-[10px] border border-[#E4E8F1] rounded-[12px]"
          onClickCapture={releaseBodyScrollOnLinkClick}
        >
          <ForceNetwork
            key={record?.scholar_id ?? record?.id ?? 'force-network'}
            record={record}
            height={460}
            highlightedLinkColor="#B6C3DC"
          />
        </div>
      </div>

      <div className="mt-[50px] lg:mt-[60px]">
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Co-Researchers in PNU</h3>
        <PnuCoResearchers active={active} data={internal} loading={loading} />
      </div>

      <div className="mt-[50px] lg:mt-[60px]">
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">External Co-Researchers</h3>
        <ExternalCoResearchers active={active} data={external} loading={loading} perPage={6} error={error} />
      </div>
    </div>
  );
}
